import React, {useEffect} from 'react';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {TextStyle, View, ViewStyle} from 'react-native';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';

import {useDispatch, useSelector} from 'react-redux';
import {projectFailure, projectSuccess} from 'redux/selectors/projectSelector';
import {useToast} from 'hooks';
import {
  resetMessage,
  addProjectRequest,
  projectTeamMember,
} from 'redux/slices/projectSlice';

import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';

import {employeeList} from 'redux/selectors/employeeSelector';

import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {ProjectForm} from 'components/project-screen/ProjectForm';
import {Icon, Text} from 'ui-kits';
import { getOrgID } from 'redux/selectors/authSelector';

type initialValuesType = {
  name: string;
  description: string;
  start_date: Date | null;
  end_date: Date | null;
  team_member: projectTeamMember[];
  sprint_duration: string;
};

const initialValues: initialValuesType = {
  name: '',
  description: '',
  start_date: null,
  end_date: null,
  team_member: [],
  sprint_duration: '',
};

export function AddProjects({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const {showToasts} = useToast();
  const dispatch = useDispatch();

  const errorAddProject = useSelector(projectFailure);
  const successAddProject = useSelector(projectSuccess);
  const employees = useSelector(employeeList);
  const org_id = useSelector(getOrgID);

  // display success message
  useEffect(() => {
    if (successAddProject) {
      showToasts(
        SUCCESS_KEY,
        getSuccessMessage(successAddProject as SuccessCodes),
        'success',
      );
      dispatch(resetMessage());
      navigation.navigate(Routes.VIEWPROJECT);
    }
  }, [successAddProject, showToasts, dispatch, navigation]);

  // display error message
  useEffect(() => {
    if (errorAddProject) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(errorAddProject as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [errorAddProject, showToasts, dispatch]);

  const handleCancel = () => {
    navigation.navigate(Routes.VIEWPROJECT);
  };

  const submitHandler = (values: initialValuesType) => {
    const payload = {
      ...values,
      start_date: values.start_date?.toISOString()!,
      end_date: values.end_date?.toISOString()!,
      org_id,
      sprint_duration: parseInt(values.sprint_duration, 10),
      is_active: true,
      team_member: values.team_member.map(item => item.id),
    };
    dispatch(addProjectRequest(payload as any));
  };

  return (
    <View style={styles.container}>
      <View style={styles.formWrapper}>
        <View style={styles.header}>
          <Text style={styles.heading}>Create New Project</Text>
          <Icon size={28} type="back_arrow" onPress={handleCancel} />
        </View>
        <ProjectForm
          initialValues={initialValues}
          onSubmit={submitHandler}
          onCancel={handleCancel}
          employees={employees}
        />
      </View>
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  formWrapper: ViewStyle;
  heading: TextStyle;
  header: ViewStyle;
};

const styles: styleType = {
  container: {
    alignItems: 'center',
    paddingHorizontal: spacing['10'],
    paddingVertical: spacing['5'],
    backgroundColor: colors.palette.white,
    flex: 1,
  },
  formWrapper: {
    maxWidth: 700,
    width: '100%',
    paddingVertical: spacing['5'],
  },
  heading: {
    color: colors.palette.brandBlue,
    fontWeight: 600,
    fontSize: 40,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};
